import { APIGet, APIPost, APIPut, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { HouseAccountType } from "pages/secure/facility/customer/context/contextTypes";

export type TGetAccount = {
  id?: number;
  account_number?: string;
  customer_id?: number;
  /** Filter by closed status. Omit / undefined = all; false = open only; true = closed only. */
  closed?: boolean;
  /** Search By
   * - account_number
   * - title
   * - customer first_name
   * - customer last_name
   */
  search?: string;
  limit?: number;
  offset?: number;
  customer_type_id?: number[];
  cart_customer_type_id?: number[];
  league_id?: number | number[];
  tournament_id?: number | number[];
  has_default_payment_method?: boolean;
};

export type TAccountSelectAllFilters = {
  search?: string;
  closed?: boolean;
  customer_type_id?: number[];
  cart_customer_type_id?: number[];
  league_id?: number | number[];
  tournament_id?: number | number[];
  has_default_payment_method?: boolean;
};

export type TPostAccount = {
  customer_id?: number;
  global?: boolean;
  title: string;
  account_number?: string;
  currency: string;
  // limit?: number;
  accounting_reference_id?: number;
};

export type TPutAccount = {
  id: number;
  title: string;
  customer_id?: number | null;
  customer_ids?: number[] | null;
  account_number?: string;
  limit?: number;
  accounting_reference_id?: number | null;
  tournament_id?: number | null;
  league_id?: number | null;
  global?: boolean;
  send_statement?: boolean;
  auto_pay_balance?: boolean;
  auto_pay_anchor_date?: number | null;
  default_payment_method_id?: number | null;
};

export type TPutSendAccountStatements = {
  send_all?: boolean;
  period?: "current" | "last_month";
  account_ids?: number[];
  select_all_filters?: TAccountSelectAllFilters;
};

export type TPutAttachMinimumSpendBatch = {
  minimum_id: number;
  account_ids?: number[];
  select_all_filters?: TAccountSelectAllFilters;
};

export type TPutAccountBulkUpdate = {
  account_ids?: number[];
  select_all_filters?: TAccountSelectAllFilters;
  limit?: number | null;
  accounting_reference_id?: number | null;
  global?: boolean;
  send_statement?: boolean;
  auto_pay_balance?: boolean;
  auto_pay_anchor_date?: number | null;
};

export type TPostAccountBatchPayment = {
  account_ids?: number[];
  select_all_filters?: TAccountSelectAllFilters;
  charge_balance: boolean;
  amount?: number;
  register_id?: number;
  send_receipt?: boolean;
};

export type TPostAccountBatchOrder = {
  account_ids?: number[];
  select_all_filters?: TAccountSelectAllFilters;
  line_items: Array<{ variant_id: number; quantity: number }>;
  register_id?: number;
  send_receipt?: boolean;
};

export type TAccountBatchJobResult = {
  account_id: number;
  status: "success" | "skipped" | "failed";
  message: string;
  amount?: number;
  order_id?: number;
  transaction_id?: number;
  account_title?: string | null;
  account_number?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
};

export type TAccountBatchJob = {
  id: number;
  type: "payment" | "order";
  title?: string | null;
  status: "draft" | "scheduled" | "pending" | "processing" | "complete" | "failed" | "partial";
  scheduled_at?: string | null;
  payload?: Record<string, unknown>;
  results?: TAccountBatchJobResult[];
  account_count?: number;
  success_count?: number;
  skipped_count?: number;
  failed_count?: number;
  created_at?: string;
  updated_at?: string;
};

export type TGetAccountBatchJobs = {
  date?: string;
  start_date?: string;
  end_date?: string;
  /** Account result status: success | failed | pending */
  status?: string | string[];
  /** Job lifecycle status: draft | scheduled | pending | processing | complete | failed */
  job_status?: string | string[];
  search?: string;
  limit?: number;
  offset?: number;
};

export type TPostAccountBatch = {
  type: "payment" | "order";
  title?: string;
  account_ids?: number[];
  select_all_filters?: TAccountSelectAllFilters;
  charge_balance?: boolean;
  amount?: number;
  line_items?: Array<{ variant_id: number; quantity: number }>;
  register_id?: number;
  send_receipt?: boolean;
};

export type TPutAccountBatch = TPostAccountBatch & {
  id: number;
  scheduled_at?: string | null;
};

export type TAccountBatchResolveImportResult = {
  matched: Array<{
    row: number;
    account_id: number;
    account_number?: string | null;
    title?: string | null;
    match_by: string;
  }>;
  ambiguous: Array<{
    row: number;
    reason: string;
    candidates: Array<{
      account_id: number;
      account_number?: string | null;
      title?: string | null;
      customer_name?: string | null;
    }>;
  }>;
  unmatched: Array<{
    row: number;
    reason: string;
  }>;
  matched_count: number;
  ambiguous_count: number;
  unmatched_count: number;
  account_ids: number[];
};

export async function GetAccount(params: TGetAccount, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<HouseAccountType[]>(
    APIUrl(AdminType.FacilityAdmin, "/client/account"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function GetAccountCount(params: TGetAccount, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<{ total: number }>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/count"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostAccount(params: TPostAccount, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/client/account"), params, useGlobalLoader);
}

export async function PutAccount(params: TPutAccount, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/account"), params, useGlobalLoader);
}

export async function PutAccountBalanceRefresh(params: { account_id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/account/update"), params, useGlobalLoader);
}

export async function PutCloseHouseAccount(params: { account_id: number }, useGlobalLoader: boolean) {
  return APIPut(APIUrl(AdminType.FacilityAdmin, "/client/account/close"), params, useGlobalLoader);
}

export async function PostImportAccounts(params: FormData, useGlobalLoader: boolean) {
  return APIPost(APIUrl(AdminType.FacilityAdmin, "/client/account/import"), params, useGlobalLoader);
}

export async function PutSendAccountStatements(params: TPutSendAccountStatements, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/account/statement/send-monthly"), params, useGlobalLoader);
}

export async function PutAttachMinimumSpendBatch(params: TPutAttachMinimumSpendBatch, useGlobalLoader: boolean) {
  return await APIPut(
    APIUrl(AdminType.FacilityAdmin, "/client/account/minimum-spending/attach-batch"),
    params,
    useGlobalLoader,
  );
}

export async function PutAccountBulkUpdate(params: TPutAccountBulkUpdate, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/account/bulk-update"), params, useGlobalLoader);
}

export async function GetAccountTransactions(params: any, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/client/account/transaction"), params, useGlobalLoader);
}

export async function PostAccountBatchPayment(params: TPostAccountBatchPayment, useGlobalLoader: boolean) {
  return await APIPost<TAccountBatchJob>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch-payment"),
    params,
    useGlobalLoader,
  );
}

export async function PostAccountBatchOrder(params: TPostAccountBatchOrder, useGlobalLoader: boolean) {
  return await APIPost<TAccountBatchJob>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch-order"),
    params,
    useGlobalLoader,
  );
}

export async function GetAccountBatchJobs(params: TGetAccountBatchJobs, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<TAccountBatchJob[]>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch-jobs"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function GetAccountBatchJob(params: { id: number }, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<TAccountBatchJob>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch-job"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostAccountBatch(params: TPostAccountBatch, useGlobalLoader: boolean) {
  return await APIPost<TAccountBatchJob>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch"),
    params,
    useGlobalLoader,
  );
}

export async function PutAccountBatch(params: TPutAccountBatch, useGlobalLoader: boolean) {
  return await APIPut<TAccountBatchJob>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch"),
    params,
    useGlobalLoader,
  );
}

export async function PostAccountBatchNow(params: { id: number }, useGlobalLoader: boolean) {
  return await APIPost<TAccountBatchJob>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch/post"),
    params,
    useGlobalLoader,
  );
}

export async function PostAccountBatchSchedule(
  params: { id: number; scheduled_at: string },
  useGlobalLoader: boolean,
) {
  return await APIPost<TAccountBatchJob>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch/schedule"),
    params,
    useGlobalLoader,
  );
}

export async function PostAccountBatchCancelSchedule(params: { id: number }, useGlobalLoader: boolean) {
  return await APIPost<TAccountBatchJob>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch/cancel-schedule"),
    params,
    useGlobalLoader,
  );
}

export async function PostAccountBatchResolveImport(params: FormData, useGlobalLoader: boolean) {
  return await APIPost<TAccountBatchResolveImportResult>(
    APIUrl(AdminType.FacilityAdmin, "/client/account/batch/resolve-import"),
    params,
    useGlobalLoader,
  );
}
